import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useThemeStore, type ThemeMode } from '@/features/theme';
import { useAuthStore, useAdminAccess } from '@/features/auth';
import {
  NotificationSoundVolume,
  playPreviewSound,
  useNotificationSoundStore,
} from '@/features/notifications';
import { ApiKeyDialog, API_KEY_STATE_LABEL, apiKeyMeta, apiKeyState } from '@/features/api-keys';
import {
  apiErrorText,
  apiKeysApi,
  notificationSettingsApi,
  usersApi,
  type ApiKey,
  type GrantableScope,
  type NotificationChannelOverride,
  type NotificationSettings,
} from '@/shared/api';
import {
  ArrowLeft,
  Ban,
  Check,
  ChevronRight,
  Eye,
  Globe,
  Lock,
  Monitor,
  Moon,
  Palette,
  Pencil,
  Plus,
  Sun,
  Trash2,
  TriangleAlert,
  Volume2,
  type LucideIcon,
} from 'lucide-react';
import {
  AdminIcon,
  ApiKeyIcon,
  EmailIcon,
  GroupIcon,
  KpiIcon,
  NotificationsIcon,
  PersonIcon,
  ReplyTemplateIcon,
  TicketIcon,
} from '@/shared/config';
import {
  responseTemplatesApi,
  type ResponseTemplate,
  type TemplateCounts,
  type TemplateScope,
} from '@/shared/api';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  PageMotion,
  Pagination,
  Scroller,
  SearchInput,
  Select,
  Skeleton,
  Tabs,
  TabsCount,
  TabsList,
  TabsTab,
  Textarea,
  confirmDialog,
  toast,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { usePageScroll } from '@/shared/lib/pageScroll';
import styles from './Settings.module.css';

const EVENT_LABELS: Record<string, string> = {
  ticket_created: 'Новая заявка',
  message_sent: 'Сообщение в чате',
  participant_added: 'Добавлен участник',
  participant_removed: 'Удалён участник',
  assignee_added: 'Назначен ответственный',
  assignee_removed: 'Снят ответственный',
  assignee_reassigned: 'Заявка переназначена',
  status_changed: 'Смена статуса заявки',
  approval_created: 'Запрошено согласование',
  approval_approved: 'Согласование одобрено',
  approval_rejected: 'Согласование отклонено',
  approval_round_approved: 'Комитет согласовал заявку',
  approval_round_rejected: 'Комитет не согласовал заявку',
  approval_level_reminded: 'Напоминание о решении по согласованию',
  group_assigned: 'Заявка назначена вашей группе',
  group_reassigned: 'Заявка передана другой группе',
  mentioned: 'Вас упомянули в чате',
  template_applied: 'Ответ по заявке',
  mass_incident_marked: 'Заявка отнесена к массовому сбою',
  workflow_notify: 'Уведомление по заявке',
  resolution_confirmed: 'Заявитель подтвердил выполнение',
  resolution_rejected: 'Заявитель не подтвердил выполнение',
  ticket_rated: 'Заявитель оценил исполнение',
  confirmation_reminded: 'Напоминание о подтверждении выполнения',
  sla_warning: 'Срок по заявке скоро истекает',
  sla_reaction_warning: 'Скоро истекает срок первого ответа',
  chat_message: 'Сообщение в мессенджере',
  folder_grant_expired: 'Истёк срок доступа к папке',
  task_assigned: 'Назначена задача',
  task_reopened: 'Задача снова в работе',
  task_ticket_finished: 'Заявка задачи закрыта',
  task_commented: 'Комментарий в задаче',
  sprint_started: 'Спринт стартовал',
  sprint_closed: 'Спринт закрыт',
  sprint_overdue: 'Спринт просрочен',
};

const EVENT_GROUPS: { title: string; types: string[] }[] = [
  {
    title: 'Заявки',
    types: [
      'ticket_created',
      'message_sent',
      'mentioned',
      'status_changed',
      'participant_added',
      'participant_removed',
      'assignee_added',
      'assignee_removed',
      'assignee_reassigned',
      'group_assigned',
      'group_reassigned',
      'template_applied',
      'mass_incident_marked',
      'workflow_notify',
      'resolution_confirmed',
      'resolution_rejected',
      'ticket_rated',
      'confirmation_reminded',
      'sla_warning',
      'sla_reaction_warning',
    ],
  },
  {
    title: 'Согласования',
    types: [
      'approval_created',
      'approval_approved',
      'approval_rejected',
      'approval_round_approved',
      'approval_round_rejected',
      'approval_level_reminded',
    ],
  },
  {
    title: 'Задачи и спринты',
    types: [
      'task_assigned',
      'task_reopened',
      'task_ticket_finished',
      'task_commented',
      'sprint_started',
      'sprint_closed',
      'sprint_overdue',
    ],
  },
  { title: 'Мессенджер и доступ', types: ['chat_message', 'folder_grant_expired'] },
];

function groupEventTypes(eventTypes: string[]): { title: string; types: string[] }[] {
  const known = new Set(EVENT_GROUPS.flatMap((g) => g.types));
  const groups = EVENT_GROUPS.map((g) => ({
    title: g.title,
    types: g.types.filter((t) => eventTypes.includes(t)),
  }));
  const rest = eventTypes.filter((t) => !known.has(t));
  if (rest.length > 0) groups.push({ title: 'Прочее', types: rest });
  return groups.filter((g) => g.types.length > 0);
}

function Switch({
  checked,
  onChange,
  label,
  'aria-label': ariaLabel,
}: {
  checked: boolean;
  onChange: () => void;
  label?: string;
  'aria-label'?: string;
}) {
  return (
    <label className={styles.switch}>
      <input type="checkbox" checked={checked} onChange={onChange} aria-label={ariaLabel} />
      <span className={styles.switchTrack} aria-hidden>
        <span className={styles.switchThumb} />
      </span>
      {label && <span className={styles.switchLabel}>{label}</span>}
    </label>
  );
}

function IconTile({ Icon, large }: { Icon: LucideIcon; large?: boolean }) {
  return (
    <span className={cn(styles.glyph, large && styles.glyphLarge)} aria-hidden>
      <Icon size={large ? 18 : 16} />
    </span>
  );
}

const Block: React.FC<{
  title?: React.ReactNode;
  aside?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, aside, footer, children }) => (
  <section className={styles.block}>
    {(title || aside) && (
      <div className={styles.blockHead}>
        {title && <h3 className={styles.blockTitle}>{title}</h3>}
        {aside}
      </div>
    )}
    <div className={styles.list}>{children}</div>
    {footer && <p className={styles.blockFoot}>{footer}</p>}
  </section>
);

const SettingRow: React.FC<{
  label: React.ReactNode;
  hint?: React.ReactNode;
  lead?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}> = ({ label, hint, lead, className, children }) => (
  <div className={cn(styles.row, className)}>
    {lead && <span className={styles.rowLead}>{lead}</span>}
    <span className={styles.rowBody}>
      <span className={styles.rowLabel}>{label}</span>
      {hint && <span className={styles.rowHint}>{hint}</span>}
    </span>
    {children && <span className={styles.rowControl}>{children}</span>}
  </div>
);

const AddRow: React.FC<{ label: string; disabled?: boolean; onClick: () => void }> = ({
  label,
  disabled,
  onClick,
}) => (
  <button type="button" className={styles.addRow} disabled={disabled} onClick={onClick}>
    <span className={styles.addIcon} aria-hidden>
      <Plus size={16} />
    </span>
    {label}
  </button>
);

const SectionPane: React.FC<{
  title: string;
  onBack?: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, onBack, actions, children }) => (
  <>
    <header className={styles.paneBar}>
      {onBack && (
        <IconButton label="К настройкам" onClick={onBack}>
          <ArrowLeft size={18} aria-hidden />
        </IconButton>
      )}
      <h2 className={styles.paneTitle}>{title}</h2>
      {actions && <div className={styles.paneActions}>{actions}</div>}
    </header>
    <div className={styles.paneBody}>{children}</div>
  </>
);

function SoundRows() {
  const enabled = useNotificationSoundStore((s) => s.enabled);
  const setEnabled = useNotificationSoundStore((s) => s.setEnabled);
  const toggle = () => {
    setEnabled(!enabled);
    if (!enabled) playPreviewSound(useNotificationSoundStore.getState().volume);
  };
  return (
    <>
      <SettingRow lead={<IconTile Icon={Volume2} />} label="Звук" hint="Для этого браузера">
        <Switch checked={enabled} onChange={toggle} aria-label="Звук уведомлений" />
      </SettingRow>
      <SettingRow lead={<span className={styles.glyphSpacer} />} label="Громкость">
        <NotificationSoundVolume />
      </SettingRow>
    </>
  );
}

const NOTIF_AUTOSAVE_DEBOUNCE_MS = 600;

type SectionProps = { onBack?: () => void };

function NotificationSettingsSection({ onBack }: SectionProps) {
  const isStaff = useAuthStore((s) => s.hasPermission)('view_tickets');
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const savedRef = useRef<NotificationSettings | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingNextRef = useRef<NotificationSettings | null>(null);
  const editVersionRef = useRef(0);
  const savedVersionRef = useRef(0);

  useEffect(() => {
    notificationSettingsApi
      .get()
      .then((res) => {
        savedRef.current = res.data;
        setSettings(res.data);
      })
      .catch(() => setLoadError(true));
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const pending = pendingNextRef.current;
      if (pending) {
        pendingNextRef.current = null;
        void notificationSettingsApi
          .update({
            inApp: pending.inApp,
            email: pending.email,
            perEvent: pending.perEvent,
            notifyGroupTickets: pending.notifyGroupTickets,
            notifyAllTickets: pending.notifyAllTickets,
          })
          .catch(() => {
          });
      }
    };
  }, []);

  if (!settings) {
    return (
      <SectionPane title="Уведомления" onBack={onBack}>
        {loadError ? (
          <ErrorState message="Не удалось загрузить настройки уведомлений" />
        ) : (
          <Block title="Каналы">
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} className={styles.row}>
                <Skeleton width={32} height={32} />
                <Skeleton height={14} width="40%" />
              </div>
            ))}
          </Block>
        )}
      </SectionPane>
    );
  }

  const eventTypes = settings.availableEventTypes ?? Object.keys(EVENT_LABELS);
  const eventGroups = groupEventTypes(eventTypes);

  const channelValue = (eventType: string, channel: 'inApp' | 'email'): boolean => {
    const override = settings.perEvent[eventType]?.[channel];
    return override ?? settings[channel];
  };

  const applyAndSave = (next: NotificationSettings) => {
    setSettings(next);
    setSaveState('saving');
    pendingNextRef.current = next;
    const version = ++editVersionRef.current;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      pendingNextRef.current = null;
      try {
        const res = await notificationSettingsApi.update({
          inApp: next.inApp,
          email: next.email,
          perEvent: next.perEvent,
          notifyGroupTickets: next.notifyGroupTickets,
          notifyAllTickets: next.notifyAllTickets,
        });
        if (version > savedVersionRef.current) {
          savedVersionRef.current = version;
          savedRef.current = { ...res.data, availableEventTypes: next.availableEventTypes };
        }
        if (editVersionRef.current === version) {
          if (savedRef.current) setSettings(savedRef.current);
          setSaveState('saved');
        }
      } catch (err) {
        if (editVersionRef.current !== version) return;
        if (savedRef.current) setSettings(savedRef.current);
        setSaveState('failed');
        toast.error(apiErrorText(err, 'Не удалось сохранить настройки'));
      }
    }, NOTIF_AUTOSAVE_DEBOUNCE_MS);
  };

  const toggleEvent = (eventType: string, channel: 'inApp' | 'email') => {
    const current = channelValue(eventType, channel);
    const nextOverride: NotificationChannelOverride = {
      ...settings.perEvent[eventType],
      [channel]: !current,
    };
    applyAndSave({
      ...settings,
      perEvent: { ...settings.perEvent, [eventType]: nextOverride },
    });
  };

  const toggleMaster = (channel: 'inApp' | 'email') => {
    applyAndSave({ ...settings, [channel]: !settings[channel] });
  };

  const toggleScope = (key: 'notifyGroupTickets' | 'notifyAllTickets') => {
    applyAndSave({ ...settings, [key]: !settings[key] });
  };

  return (
    <SectionPane
      title="Уведомления"
      onBack={onBack}
      actions={
        <span className={styles.saveState} role="status" data-state={saveState}>
          {saveState === 'saving'
            ? 'Сохраняется…'
            : saveState === 'saved'
              ? 'Сохранено'
              : saveState === 'failed'
                ? 'Не сохранилось'
                : 'Сохраняется автоматически'}
        </span>
      }
    >
      <Block
        title="Каналы"
        footer="Значение по умолчанию для всех событий. Отдельное событие можно переопределить ниже."
      >
        <SettingRow
          lead={<IconTile Icon={NotificationsIcon} />}
          label="В приложении"
          hint="Колокольчик в шапке и лента уведомлений"
        >
          <Switch checked={settings.inApp} onChange={() => toggleMaster('inApp')} aria-label="В приложении" />
        </SettingRow>
        <SettingRow
          lead={<IconTile Icon={EmailIcon} />}
          label="На почту"
          hint="Письмо на адрес из учётной записи"
        >
          <Switch checked={settings.email} onChange={() => toggleMaster('email')} aria-label="На почту" />
        </SettingRow>
        <SoundRows />
      </Block>

      {isStaff && (
        <Block title="Новые заявки">
          <SettingRow
            lead={<IconTile Icon={GroupIcon} />}
            label="Моей группы"
            hint="Заявки, назначенные на вашу группу поддержки"
          >
            <Switch
              checked={settings.notifyGroupTickets}
              onChange={() => toggleScope('notifyGroupTickets')}
              aria-label="Новые заявки моей группы"
            />
          </SettingRow>
          <SettingRow
            lead={<IconTile Icon={TicketIcon} />}
            label="Все"
            hint="Каждая новая заявка, даже вне вашей группы"
          >
            <Switch
              checked={settings.notifyAllTickets}
              onChange={() => toggleScope('notifyAllTickets')}
              aria-label="Все новые заявки"
            />
          </SettingRow>
        </Block>
      )}

      {eventGroups.map((group, i) => (
        <Block
          key={group.title}
          title={group.title}
          aside={
            <span className={styles.eventCols} aria-hidden>
              <span className={styles.eventCol}>Приложение</span>
              <span className={styles.eventCol}>Почта</span>
            </span>
          }
          footer={
            i === eventGroups.length - 1
              ? 'Тумблер события перебивает общий канал: можно выключить почту везде и оставить её для смены статуса.'
              : undefined
          }
        >
          {group.types.map((eventType) => {
            const label = EVENT_LABELS[eventType] ?? eventType;
            return (
              <div key={eventType} className={cn(styles.row, styles.eventRow)}>
                <span className={styles.rowLabel}>{label}</span>
                <span className={styles.eventCol}>
                  <Switch
                    checked={channelValue(eventType, 'inApp')}
                    onChange={() => toggleEvent(eventType, 'inApp')}
                    aria-label={`В приложении: ${label}`}
                  />
                </span>
                <span className={styles.eventCol}>
                  <Switch
                    checked={channelValue(eventType, 'email')}
                    onChange={() => toggleEvent(eventType, 'email')}
                    aria-label={`Почта: ${label}`}
                  />
                </span>
              </div>
            );
          })}
        </Block>
      ))}
    </SectionPane>
  );
}

const THEMES: { value: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Светлая', Icon: Sun },
  { value: 'dark', label: 'Тёмная', Icon: Moon },
  { value: 'system', label: 'Как в системе', Icon: Monitor },
];

function ThemeMini({ variant, className }: { variant: 'light' | 'dark'; className?: string }) {
  return (
    <span className={cn(styles.themePreview, className)} data-variant={variant} aria-hidden>
      <span className={styles.previewSide} />
      <span className={styles.previewMain}>
        <span className={styles.previewBar} />
        <span className={styles.previewBubble} />
        <span className={cn(styles.previewBubble, styles.previewBubbleOwn)} />
      </span>
    </span>
  );
}

function AppearanceSection({ onBack }: SectionProps) {
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  return (
    <SectionPane title="Внешний вид" onBack={onBack}>
      <Block
        title="Оформление"
        footer="«Как в системе» следует за настройкой операционной системы и переключается вместе с ней."
      >
        <div className={styles.themeGrid} role="radiogroup" aria-label="Цветовая схема">
          {THEMES.map((t) => {
            const active = mode === t.value;
            return (
              <button
                key={t.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={cn(styles.themeCard, active && styles.themeActive)}
                onClick={() => setMode(t.value)}
              >
                {t.value === 'system' ? (
                  <span className={styles.previewSplit} aria-hidden>
                    <ThemeMini variant="light" />
                    <ThemeMini variant="dark" className={styles.previewHalf} />
                  </span>
                ) : (
                  <ThemeMini variant={t.value} />
                )}
                <span className={styles.themeMeta}>
                  <t.Icon size={14} aria-hidden />
                  <span className={styles.themeLabel}>{t.label}</span>
                </span>
                <span className={styles.themeRadio} aria-hidden>
                  {active && <Check size={12} strokeWidth={3} />}
                </span>
              </button>
            );
          })}
        </div>
      </Block>
    </SectionPane>
  );
}

const VARIABLES: { key: string; label: string }[] = [
  { key: 'ticket.subject', label: 'Тема заявки' },
  { key: 'creator.name', label: 'Имя заявителя' },
  { key: 'actor.name', label: 'Моё имя' },
  { key: 'ticket.status', label: 'Статус' },
  { key: 'ticket.priority', label: 'Приоритет' },
  { key: 'date', label: 'Дата' },
  { key: 'time', label: 'Время' },
];

function renderPreview(body: string, actorName: string): string {
  const now = new Date();
  const sample: Record<string, string> = {
    'ticket.subject': 'Не открывается VPN',
    'creator.name': 'Иван Петров',
    'actor.name': actorName || 'Вы',
    'ticket.status': 'В работе',
    'ticket.priority': 'Высокий',
    date: now.toLocaleDateString('ru'),
    time: now.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' }),
  };
  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key: string) => sample[key] ?? m);
}

type EditorState =
  | { open: false }
  | { open: true; template: ResponseTemplate | null; scope?: TemplateScope; groupId?: string | null };

type TemplateGroup = { id: string; name: string };

function TemplateModal({
  state,
  actorName,
  canManageShared,
  groups,
  onClose,
  onSaved,
}: {
  state: EditorState;
  actorName: string;
  canManageShared: boolean;
  groups: TemplateGroup[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const open = state.open;
  const template = state.open ? state.template : null;
  const presetScope = state.open ? state.scope : undefined;
  const presetGroupId = state.open ? state.groupId : undefined;
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<TemplateScope>('personal');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? '');
    setBody(template?.body ?? '');
    setScope(template?.scope ?? presetScope ?? 'personal');
    setGroupId(template?.groupId ?? presetGroupId ?? groups[0]?.id ?? null);
    setError(null);
  }, [open, template, presetScope, presetGroupId, groups]);

  const scopeOptions: TemplateScope[] = [
    'personal',
    ...(groups.length > 0 ? (['group'] as const) : []),
    ...(canManageShared ? (['shared'] as const) : []),
  ];
  const canPickScope = scopeOptions.length > 1 && (!template || template.scope !== 'shared' || canManageShared);
  const scopePatch = canPickScope ? { scope, groupId: scope === 'group' ? groupId : null } : {};

  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + token.length;
    });
  };

  const handleSave = async () => {
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (template) {
        await responseTemplatesApi.update(template.id, { name: name.trim(), body, ...scopePatch });
      } else {
        await responseTemplatesApi.create({ name: name.trim(), body, ...scopePatch });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Ошибка сохранения шаблона'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={styles.modal}>
        <DialogTitle>{template ? 'Правка шаблона' : 'Новый шаблон'}</DialogTitle>

        {error && <p className={styles.errorNote}>{error}</p>}

        {canPickScope && (
          <div className={styles.scopeToggle} role="radiogroup" aria-label="Кому виден шаблон">
            {scopeOptions.map((sc) => {
              const Icon = sc === 'personal' ? Lock : sc === 'group' ? GroupIcon : Globe;
              return (
                <button
                  key={sc}
                  type="button"
                  role="radio"
                  aria-checked={scope === sc}
                  className={cn(styles.scopeBtn, scope === sc && styles.scopeActive)}
                  onClick={() => setScope(sc)}
                >
                  <Icon size={13} aria-hidden /> {TPL_SCOPE_LABEL[sc]}
                </button>
              );
            })}
          </div>
        )}
        {canPickScope && scope === 'group' && groups.length > 1 && (
          <Field label="Группа">
            <Select
              value={groupId ?? ''}
              onValueChange={setGroupId}
              options={groups.map((g) => ({ value: g.id, label: g.name }))}
              aria-label="Группа шаблона"
            />
          </Field>
        )}
        {canPickScope && scope !== 'personal' && (
          <p className={styles.scopeNote}>
            {scope === 'group'
              ? `Увидят все участники группы «${groups.find((g) => g.id === groupId)?.name ?? ''}», править и удалять сможете только вы.`
              : 'Увидят все сотрудники поддержки, править могут держатели права на общие шаблоны.'}
          </p>
        )}

        <Field label="Название" required>
          <Input
            id="tpl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Приветствие"
            autoFocus
          />
        </Field>

        <Field label="Текст шаблона" required>
          <Textarea
            id="tpl-body"
            ref={bodyRef}
            className={styles.bodyArea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={'Здравствуйте, {{creator.name}}! По заявке «{{ticket.subject}}»…'}
            rows={5}
          />
        </Field>

        <div className={styles.varsRow}>
          <span className={styles.varsLabel}>Вставить переменную</span>
          {VARIABLES.map((v) => (
            <button
              key={v.key}
              type="button"
              className={styles.varChip}
              onClick={() => insertVariable(v.key)}
              title={`Вставить {{${v.key}}}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {body.trim() && (
          <div className={styles.previewBox}>
            <span className={styles.previewLabel}>Предпросмотр</span>
            <Scroller className={styles.preview}>{renderPreview(body, actorName)}</Scroller>
          </div>
        )}

        <DialogFooter>
          <DialogClose render={<Button type="button" variant="ghost">Отмена</Button>} />
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !name.trim() || !body.trim()}
          >
            {saving ? 'Сохранение…' : template ? 'Сохранить' : 'Создать шаблон'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TPL_SCOPE_LABEL: Record<TemplateScope, string> = {
  personal: 'Личный',
  group: 'Группа',
  shared: 'Общий',
};

const TPL_PAGE_SIZE = 20;

const TPL_FOOTER: Record<TemplateScope, string> = {
  personal:
    'Видите только вы. Заготовки применяются кнопкой шаблонов в строке ввода чата заявки, переменные подставляются сами.',
  group:
    'Видят все участники группы — коллеги ответят вашей заготовкой, пока вы в отпуске. Создать может каждый, править и удалять — только автор.',
  shared: 'Видят все сотрудники поддержки, правят держатели права на общие шаблоны.',
};

function ResponseTemplatesSection({ actorName, onBack }: SectionProps & { actorName: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageShared = hasPermission('manage_shared_templates');
  const user = useAuthStore((s) => s.user);

  const [tab, setTab] = useState<TemplateScope>('personal');
  const [groups, setGroups] = useState<TemplateGroup[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query.trim(), 300);
  const [page, setPage] = useState(1);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<TemplateCounts | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [editor, setEditor] = useState<EditorState>({ open: false });
  const reqRef = useRef(0);

  const activeGroupId = tab === 'group' ? (groupId ?? groups[0]?.id ?? null) : null;

  const load = useCallback(async () => {
    const req = ++reqRef.current;
    try {
      const res = await responseTemplatesApi.list({
        scope: tab,
        groupId: activeGroupId ?? undefined,
        q: q || undefined,
        limit: TPL_PAGE_SIZE,
        offset: (page - 1) * TPL_PAGE_SIZE,
      });
      if (req !== reqRef.current) return;
      setTemplates(Array.isArray(res.data) ? res.data : []);
      setTotal(res.total ?? 0);
      setCounts(res.counts ?? null);
      setGroups(res.groups ?? []);
      setLoadError(null);
    } catch (err) {
      if (req === reqRef.current) setLoadError(err);
    } finally {
      if (req === reqRef.current) setLoaded(true);
    }
  }, [tab, activeGroupId, q, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = () =>
    load().catch((err) => toast.error(apiErrorText(err, 'Не удалось обновить список шаблонов')));

  const pickTab = (next: TemplateScope) => {
    setTab(next);
    setPage(1);
  };

  const canEdit = (t: ResponseTemplate): boolean =>
    t.scope === 'shared' ? canManageShared : t.ownerId === user?.id;

  const handleDelete = async (t: ResponseTemplate) => {
    const ok = await confirmDialog({
      title: `Удалить шаблон «${t.name}»?`,
      description:
        t.scope === 'shared'
          ? 'Общий шаблон пропадёт у всех сотрудников. Действие необратимо.'
          : t.scope === 'group'
            ? 'Шаблон пропадёт у всех участников группы. Действие необратимо.'
            : 'Действие необратимо.',
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    try {
      await responseTemplatesApi.remove(t.id);
      if (templates.length === 1 && page > 1) setPage(page - 1);
      else await reload();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось удалить шаблон'));
    }
  };

  const author = (t: ResponseTemplate) =>
    t.scope === 'personal' ? null : t.ownerId === user?.id ? 'Вы' : (t.authorName ?? null);

  const renderRow = (t: ResponseTemplate) => {
    const by = author(t);
    return (
      <SettingRow
        key={t.id}
        className={canEdit(t) ? styles.tplRow : undefined}
        label={
          canEdit(t) ? (
            <button type="button" className={styles.tplOpen} onClick={() => setEditor({ open: true, template: t })}>
              {t.name}
            </button>
          ) : (
            t.name
          )
        }
        hint={
          <>
            <span className={styles.tplBody}>{t.body}</span>
            {by && (
              <span className={styles.tplAuthor}>
                <PersonIcon size={12} aria-hidden />
                {by}
              </span>
            )}
          </>
        }
      >
        {canEdit(t) && (
          <>
            <IconButton
              label={`Изменить шаблон «${t.name}»`}
              tooltip="Изменить"
              size="sm"
              onClick={() => setEditor({ open: true, template: t })}
            >
              <Pencil size={14} aria-hidden />
            </IconButton>
            <IconButton
              label={`Удалить шаблон «${t.name}»`}
              tooltip="Удалить"
              tone="danger"
              size="sm"
              onClick={() => void handleDelete(t)}
            >
              <Trash2 size={14} aria-hidden />
            </IconButton>
          </>
        )}
      </SettingRow>
    );
  };

  const groupName = groups.find((g) => g.id === activeGroupId)?.name ?? '';
  const canAdd = tab !== 'shared' || canManageShared;
  const addLabel =
    tab === 'group' ? 'Новый шаблон группы' : tab === 'shared' ? 'Новый общий шаблон' : 'Новый шаблон';
  const emptyTitle = q
    ? 'Ничего не нашлось'
    : tab === 'group'
      ? `В группе «${groupName}» шаблонов пока нет`
      : tab === 'shared'
        ? 'Общих шаблонов пока нет'
        : 'Личных шаблонов пока нет';
  const emptyHint = q
    ? 'Поиск идёт по названию и тексту шаблона.'
    : tab === 'group'
      ? 'Заготовка группы выручит коллег, пока вы в отпуске.'
      : tab === 'shared'
        ? 'Общий шаблон появится у каждого сотрудника в строке ввода чата.'
        : 'Заготовка экономит время на однотипных ответах: приветствие, запрос доступа, отказ.';
  const totalPages = Math.max(1, Math.ceil(total / TPL_PAGE_SIZE));
  const groupTotal = counts ? Object.values(counts.groups).reduce((a, b) => a + b, 0) : 0;

  return (
    <SectionPane title="Шаблоны ответов" onBack={onBack}>
      <div className={styles.tplBar}>
        <Tabs value={tab} onValueChange={(v) => pickTab(v as TemplateScope)}>
          <TabsList>
            <TabsTab value="personal">
              Мои {counts && <TabsCount value={counts.personal} />}
            </TabsTab>
            {groups.length > 0 && (
              <TabsTab value="group">
                Группа {counts && <TabsCount value={groupTotal} />}
              </TabsTab>
            )}
            <TabsTab value="shared">
              Общие {counts && <TabsCount value={counts.shared} />}
            </TabsTab>
          </TabsList>
        </Tabs>
        <SearchInput
          className={styles.tplSearch}
          label="Поиск по шаблонам"
          placeholder="Название или текст"
          value={query}
          onChange={(v) => {
            setQuery(v);
            setPage(1);
          }}
        />
      </div>

      {tab === 'group' && groups.length > 1 && (
        <Select
          className={styles.tplGroups}
          aria-label="Группа шаблонов"
          value={activeGroupId ?? ''}
          onValueChange={(v) => {
            setGroupId(v);
            setPage(1);
          }}
          options={groups.map((g) => ({
            value: g.id,
            label: counts ? `${g.name} · ${counts.groups[g.id] ?? 0}` : g.name,
          }))}
        />
      )}

      <Block footer={TPL_FOOTER[tab]}>
        {!loaded ? (
          <div className={styles.tplSkeleton}>
            <Skeleton height={44} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </div>
        ) : loadError && templates.length === 0 ? (
          <ErrorState message={apiErrorText(loadError, 'Не удалось загрузить шаблоны')} onRetry={() => void reload()} />
        ) : templates.length === 0 ? (
          <EmptyState compact Icon={tab === 'group' ? GroupIcon : tab === 'shared' ? Globe : ReplyTemplateIcon} title={emptyTitle} hint={emptyHint} />
        ) : (
          templates.map(renderRow)
        )}
        {canAdd && (
          <AddRow
            label={addLabel}
            onClick={() =>
              setEditor({ open: true, template: null, scope: tab, groupId: activeGroupId })
            }
          />
        )}
      </Block>

      {totalPages > 1 && (
        <Pagination
          className={styles.tplPagination}
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={TPL_PAGE_SIZE}
          itemsLabel="шаблонов"
          onChange={setPage}
        />
      )}

      <TemplateModal
        state={editor}
        actorName={actorName}
        canManageShared={canManageShared}
        groups={groups}
        onClose={() => setEditor({ open: false })}
        onSaved={() => void reload()}
      />
    </SectionPane>
  );
}

function ApiKeysSection({ onBack }: SectionProps) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [grantable, setGrantable] = useState<GrantableScope[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await apiKeysApi.listMine();
      setKeys(res.data.keys);
      setGrantable(res.data.grantable);
      setUnavailable(res.data.personal && !res.data.personal.available ? res.data.personal.reason ?? 'не настроено' : null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const revoke = async (key: ApiKey) => {
    const ok = await confirmDialog({
      title: `Отозвать ключ «${key.name}»?`,
      description: 'Интеграция, которая ходит по нему, сразу получит отказ. Вернуть ключ нельзя — только выпустить новый.',
      confirmLabel: 'Отозвать',
      danger: true,
    });
    if (!ok) return;
    try {
      await apiKeysApi.revokeMine(key.id);
      await load();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отозвать ключ'));
    }
  };

  const active = keys.filter((k) => apiKeyState(k) === 'active');
  const inactive = keys.filter((k) => apiKeyState(k) !== 'active');

  const renderRow = (key: ApiKey) => {
    const state = apiKeyState(key);
    return (
      <SettingRow
        key={key.id}
        lead={<IconTile Icon={ApiKeyIcon} />}
        label={
          <>
            {key.name}
            {state !== 'active' && (
              <>
                {' '}
                <Badge variant="outline">{API_KEY_STATE_LABEL[state]}</Badge>
              </>
            )}
          </>
        }
        hint={apiKeyMeta(key)}
      >
        {state === 'active' && (
          <IconButton
            label={`Отозвать ключ «${key.name}»`}
            tooltip="Отозвать"
            tone="danger"
            size="sm"
            onClick={() => void revoke(key)}
          >
            <Ban size={14} aria-hidden />
          </IconButton>
        )}
      </SettingRow>
    );
  };

  return (
    <SectionPane title="API-ключи" onBack={onBack}>
      {unavailable && (
        <Callout tone="warning" Icon={TriangleAlert} title="Личные ключи сейчас не работают">
          Порталу нужен доступ к каталогу Keycloak, чтобы проверять, что владелец ключа не отключён. Сейчас его нет:{' '}
          {unavailable}. Остальной портал работает; ключи интеграций выпускает администратор.
        </Callout>
      )}
      <Block
        title="Действующие"
        footer="Для интеграций со своими скриптами и системами. Ключ действует от вашего имени и не шире ваших прав."
      >
        {loadError && keys.length === 0 ? (
          <ErrorState message={apiErrorText(loadError, 'Не удалось загрузить ключи')} onRetry={() => void load()} />
        ) : !loaded ? (
          <>
            <div className={styles.row}>
              <Skeleton width={32} height={32} />
              <Skeleton height={14} width="45%" />
            </div>
            <div className={styles.row}>
              <Skeleton width={32} height={32} />
              <Skeleton height={14} width="35%" />
            </div>
          </>
        ) : active.length === 0 ? (
          <EmptyState
            compact
            Icon={ApiKeyIcon}
            title="Действующих ключей нет"
            hint="Выпустите ключ, когда скрипту или системе понадобится доступ к порталу."
          />
        ) : (
          active.map(renderRow)
        )}
        <AddRow label="Выпустить ключ" disabled={unavailable != null} onClick={() => setCreating(true)} />
      </Block>

      {inactive.length > 0 && (
        <Block title="Отозванные и истёкшие" footer="По ним уже нельзя войти. Список — чтобы было видно, что выдавалось.">
          {inactive.map(renderRow)}
        </Block>
      )}

      <ApiKeyDialog
        open={creating}
        mode="personal"
        grantable={grantable}
        onClose={() => setCreating(false)}
        onIssued={() => void load()}
      />
    </SectionPane>
  );
}

function ShowIdentityBlock() {
  const shown = useAuthStore((s) => s.user?.showIdentity ?? false);
  const setShowIdentity = useAuthStore((s) => s.setShowIdentity);
  const [saving, setSaving] = useState(false);
  const toggle = () => {
    if (saving) return;
    const next = !shown;
    setShowIdentity(next);
    setSaving(true);
    usersApi
      .setShowIdentity(next)
      .catch((err) => {
        setShowIdentity(!next);
        toast.error(apiErrorText(err, 'Не удалось сохранить настройку'));
      })
      .finally(() => setSaving(false));
  };
  return (
    <Block
      title="Заявителям"
      footer={
        shown
          ? 'Заявители видят ваше имя и фото, по клику на имя — карточку с отделом, группой и контактами. Показатели и история заявок им не видны.'
          : 'Заявители видят вместо вас ник «Инженер …», он меняется каждый день.'
      }
    >
      <SettingRow lead={<IconTile Icon={Eye} />} label="Показывать моё имя">
        <Switch checked={shown} onChange={toggle} aria-label="Показывать моё имя заявителям" />
      </SettingRow>
    </Block>
  );
}

function AccountSection({ onBack }: SectionProps) {
  const user = useAuthStore((s) => s.user);
  const isStaff = useAuthStore((s) => s.hasPermission('view_tickets'));
  if (!user) return null;
  const name = user.name || user.email || '?';
  return (
    <SectionPane title="Аккаунт" onBack={onBack}>
      <div className={styles.hero}>
        <Avatar className={styles.heroAvatar} name={name} seed={user.id} src={user.avatarUrl} />
        <h3 className={styles.heroName}>{name}</h3>
        {user.name && user.email && <p className={styles.heroSub}>{user.email}</p>}
        {user.roles.length > 0 && (
          <span className={styles.roleRow}>
            {user.roles.map((role) => (
              <Badge key={role} variant="outline">{role}</Badge>
            ))}
          </span>
        )}
      </div>
      <Block footer="Имя, почта и роли приходят из единого входа и меняются там же.">
        <Link to={`/users/${user.id}`} className={styles.linkRow}>
          <SettingRow lead={<IconTile Icon={PersonIcon} />} label="Мой профиль" hint="Как вас видят коллеги">
            <ChevronRight size={16} aria-hidden className={styles.chevron} />
          </SettingRow>
        </Link>
      </Block>
      {isStaff && <ShowIdentityBlock />}
    </SectionPane>
  );
}

type SectionId = 'appearance' | 'notifications' | 'templates' | 'api-keys' | 'account';

const SECTION_META: Record<Exclude<SectionId, 'account'>, { label: string; Icon: LucideIcon }> = {
  appearance: { label: 'Внешний вид', Icon: Palette },
  notifications: { label: 'Уведомления', Icon: NotificationsIcon },
  templates: { label: 'Шаблоны ответов', Icon: ReplyTemplateIcon },
  'api-keys': { label: 'API-ключи', Icon: ApiKeyIcon },
};

const NavItem: React.FC<{
  active: boolean;
  lead: React.ReactNode;
  label: React.ReactNode;
  hint?: React.ReactNode;
  onClick: () => void;
}> = ({ active, lead, label, hint, onClick }) => (
  <button
    type="button"
    className={cn(styles.navItem, active && styles.navItemActive)}
    aria-current={active ? 'page' : undefined}
    onClick={onClick}
  >
    {lead}
    <span className={styles.navBody}>
      <span className={styles.navLabel}>{label}</span>
      {hint && <span className={styles.navHint}>{hint}</span>}
    </span>
    <ChevronRight size={16} aria-hidden className={styles.navChevron} />
  </button>
);

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const isStaff = hasPermission('view_tickets');
  const canApiKeys = hasPermission('manage_api_keys');
  const { canAccess: showAdmin } = useAdminAccess();
  const mode = useThemeStore((s) => s.mode);
  const narrow = usePageScroll();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const sections: SectionId[] = [
    'appearance',
    'notifications',
    ...(isStaff ? (['templates'] as const) : []),
    ...(canApiKeys ? (['api-keys'] as const) : []),
    'account',
  ];
  const raw = params.get('tab') ?? '';
  const chosen = (sections as string[]).includes(raw) ? (raw as SectionId) : null;
  const tab: SectionId | null = chosen ?? (narrow ? null : 'appearance');
  const actorName = useMemo(() => user?.name || user?.email || 'Вы', [user]);

  const open = (id: SectionId) => {
    if (id === tab) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', id);
        return next;
      },
      { replace: !narrow, state: narrow ? { settingsFromList: true } : undefined },
    );
  };
  const back = () => {
    if ((location.state as { settingsFromList?: boolean } | null)?.settingsFromList) {
      navigate(-1);
      return;
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true },
    );
  };
  const onBack = narrow ? back : undefined;

  const hints: Partial<Record<SectionId, string>> = {
    appearance: THEMES.find((t) => t.value === mode)?.label,
    notifications: 'Каналы, звук и события',
    templates: 'Заготовки для чата заявки',
    'api-keys': 'Доступ для интеграций',
  };

  const name = user ? user.name || user.email || '?' : '';

  return (
    <PageMotion className={styles.motion}>
      <main className={styles.page} data-screen={tab ? 'section' : 'list'}>
        <Card padded={false} className={styles.nav}>
          <h1 className={styles.navTitle}>Настройки</h1>

          {user && (
            <button
              type="button"
              className={cn(styles.profile, tab === 'account' && styles.profileActive)}
              aria-current={tab === 'account' ? 'page' : undefined}
              onClick={() => open('account')}
            >
              <Avatar className={styles.profileAvatar} name={name} seed={user.id} src={user.avatarUrl} />
              <span className={styles.navBody}>
                <span className={styles.profileName}>{name}</span>
                <span className={styles.navHint}>{user.name && user.email ? user.email : 'Учётная запись'}</span>
              </span>
              <ChevronRight size={16} aria-hidden className={styles.navChevron} />
            </button>
          )}

          <nav className={styles.navGroup} aria-label="Разделы настроек">
            {sections
              .filter((id): id is Exclude<SectionId, 'account'> => id !== 'account')
              .map((id) => {
                const meta = SECTION_META[id];
                return (
                  <NavItem
                    key={id}
                    active={tab === id}
                    lead={<IconTile Icon={meta.Icon} large />}
                    label={meta.label}
                    hint={hints[id]}
                    onClick={() => open(id)}
                  />
                );
              })}
          </nav>

          { }
          {(isStaff || showAdmin) && (
            <nav className={styles.navGroup} aria-label="Разделы приложения">
              {isStaff && (
                <Link to="/kpi" className={styles.navItem}>
                  <IconTile Icon={KpiIcon} large />
                  <span className={styles.navBody}>
                    <span className={styles.navLabel}>KPI инженеров</span>
                  </span>
                  <ChevronRight size={16} aria-hidden className={styles.navChevronAlways} />
                </Link>
              )}
              {showAdmin && (
                <Link to="/admin" className={styles.navItem}>
                  <IconTile Icon={AdminIcon} large />
                  <span className={styles.navBody}>
                    <span className={styles.navLabel}>Администрирование</span>
                  </span>
                  <ChevronRight size={16} aria-hidden className={styles.navChevronAlways} />
                </Link>
              )}
            </nav>
          )}

          <p className={styles.navFoot}>Правки применяются сразу — кнопки сохранения нет.</p>
        </Card>

        {tab && (
          <Card key={tab} padded={false} className={styles.pane}>
            {tab === 'appearance' && <AppearanceSection onBack={onBack} />}
            {tab === 'notifications' && <NotificationSettingsSection onBack={onBack} />}
            {tab === 'templates' && <ResponseTemplatesSection actorName={actorName} onBack={onBack} />}
            {tab === 'api-keys' && <ApiKeysSection onBack={onBack} />}
            {tab === 'account' && <AccountSection onBack={onBack} />}
          </Card>
        )}
      </main>
    </PageMotion>
  );
}
